<template>
  <div class="dy-native-phone-preview">
    <div v-if="showTitleBar" class="preview-title-bar">
      <span class="preview-title-text">{{ title }}</span>
      <a-tooltip v-if="titleTooltip" :title="titleTooltip">
        <InfoCircleOutlined class="title-info-icon" />
      </a-tooltip>
    </div>
    <div class="phone-root">
      <img class="phone-img" :src="iphoneImg" alt="" />
      <div class="tier-panel">
        <slot name="paid">
          <div v-if="!displayTiers.length" class="paid-empty">暂无付费档位</div>
          <div
            v-else
            class="tier-section"
            :class="layoutClass"
          >
            <div
              v-for="(tier, idx) in displayTiers"
              :key="tier.key"
              class="tier-card"
              :class="{ 'is-selected': idx === selectedIndex }"
              @click="onSelectTier(idx)"
            >
              <span v-if="idx === 0" class="tag-rec">推荐</span>
              <div class="tier-title">{{ tier.unlockLabel }}</div>
              <div class="tier-price">{{ tier.priceLabel }}</div>
              <div class="tier-save">{{ tier.saveLabel }}</div>
            </div>
          </div>
        </slot>
      </div>
    </div>
    <p class="disclaimer">{{ disclaimerText }}</p>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { InfoCircleOutlined } from '@ant-design/icons-vue'
import iphoneImg from '@/assets/img/iphone.png'

const props = defineProps({
  config: {
    type: Object,
    default: () => ({ tiers: [] })
  },
  selectedTierIndex: {
    type: Number,
    default: 0
  },
  title: {
    type: String,
    default: '配置预览'
  },
  showTitleBar: {
    type: Boolean,
    default: true
  },
  titleTooltip: {
    type: String,
    default: '本页面仅供参考，用户支付方式以产品页面实际展示为准。'
  },
  disclaimerText: {
    type: String,
    default:
      '上图仅为功能示意图，平台会根据CP方设置的单价和折扣，自行组合档位并对外售卖'
  }
})

const emit = defineEmits(['update:selectedTierIndex'])

const displayTiers = computed(() => {
  const list = props.config?.tiers || []
  return list.map((t, i) => {
    const u = t.unlockEpisodes
    const hasEp = u != null && Number(u) >= 1
    const pay = t.payDiscountedTotal
    const hasPay = pay != null && Number.isFinite(Number(pay))
    const save = t.saveYuan
    const hasSave = save != null && Number(save) > 0.001
    return {
      key: t.key != null ? t.key : `t-${i}`,
      unlockLabel: hasEp ? `解锁${u}集` : '解锁--集',
      priceLabel: hasPay ? `${formatMoney(pay)}元` : '-元',
      saveLabel: !hasEp || !hasPay ? '省--元' : hasSave ? `省${formatMoney(save)}元` : '无优惠'
    }
  })
})

const layoutClass = computed(() => {
  const n = displayTiers.value.length
  if (n === 2) return 'tier-layout--2'
  if (n === 3) return 'tier-layout--3'
  if (n === 4) return 'tier-layout--4'
  return 'tier-layout--fallback'
})

const selectedIndex = computed({
  get() {
    const n = displayTiers.value.length
    if (!n) return 0
    return Math.min(Math.max(0, props.selectedTierIndex), n - 1)
  },
  set(v) {
    emit('update:selectedTierIndex', v)
  }
})

function formatMoney(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n.toFixed(2) : '0.00'
}

function onSelectTier(idx) {
  selectedIndex.value = idx
}
</script>

<style scoped>
.dy-native-phone-preview {
  width: 100%;
}
.preview-title-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 15px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
}
.title-info-icon {
  color: rgba(0, 0, 0, 0.45);
  cursor: help;
  font-size: 14px;
}
.phone-root {
  position: relative;
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
}
.phone-img {
  display: block;
  width: 100%;
  height: auto;
  vertical-align: top;
}
/* 单层包裹：叠在示意图抽屉上部档位区（原 screen + strip 合并为相对 phone-root 的定位） */
.tier-panel {
  position: absolute;
  left: 10%;
  right: 10%;
  bottom: 27%;
  max-height: 24%;
  min-height: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2px 4px;
  box-sizing: border-box;
}
.paid-empty {
  width: 100%;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.35);
  font-size: 11px;
}
.tier-section {
  width: 100%;
  max-height: 100%;
  gap: 8px;
  overflow-x: hidden;
  overflow-y: auto;
  align-content: start;
}
.tier-layout--2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
}
.tier-layout--3 {
  display: grid;
  grid-template-columns: 1fr 1fr;
}
.tier-layout--3 .tier-card:nth-child(1) {
  grid-column: 1 / -1;
}
.tier-layout--4 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto;
}
.tier-layout--fallback {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.tier-card {
  position: relative;
  min-height: 0;
  border-radius: 7px;
  background: #fff;
  border: 1.5px solid #ffb3c7;
  color: #333;
  padding: 8px 8px 7px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}
.tier-card.is-selected {
  border-color: #ff2d55;
  box-shadow: 0 0 0 1px rgba(255, 45, 85, 0.15);
}
.tag-rec {
  position: absolute;
  top: 4px;
  right: 5px;
  font-size: 10px;
  background: #ff2d55;
  color: #fff;
  padding: 0 4px;
  border-radius: 2px;
  line-height: 1.35;
}
.tier-title {
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.75);
  padding-right: 22px;
}
.tier-price {
  font-size: 15px;
  font-weight: 700;
  margin-top: 2px;
  color: #000;
  line-height: 1.2;
}
.tier-save {
  font-size: 10px;
  color: rgba(0, 0, 0, 0.45);
  margin-top: 2px;
  line-height: 1.2;
}
.disclaimer {
  margin-top: 12px;
  font-size: 11px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.45);
  text-align: center;
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
}
</style>
